import React, { useState, useEffect } from "react";

// components
import Collapse from "@/components/Collapse";
import GettingStartedPreview from "./components/GettingStartedPreview";
import GettingStartedStep from "./components/GettingStartedStep";
import GettingStartedModal from "@/components/Modals/GettingStartedModal";
import SelectCampaignModal from "@/components/Modals/SelectCampaignModal";

// helpers
import useUserProfile from "@/hooks/useUserProfile";
import useRouter from "@/hooks/useRouter";
import { GETTING_STARTED_STEPS } from "./constants";
import { useLockoutModalContext } from "@/contexts/LockoutModalContext";

// assets
import styles from "@/assets/styles/containers/getting-started/index.module.scss";


type TModalConfig = {
  title: string;
  src: string;
  open: boolean;
  buttonText: string;
  modalHint: string;
  stepKey?: string;
};

const MAP_PERCENTAGE = [0, 5, 20, 55, 90, 100];

function GettingStarted() {
  // used instead of useDimension hook because need only one time default value
  const isNotMobile = typeof window !== "undefined" && window.innerWidth > 768;

  const [isOpen, setIsOpen] = useState(isNotMobile);
  const [modalConfig, setModalConfig] = useState<TModalConfig | null>(null);
  const [isSaveToShortListModalOpen, setIsSaveToShortListModalOpen] = useState(false);

  const router = useRouter();
  const { isAuthorized, data } = useUserProfile();
  const { setState } = useLockoutModalContext();

  const currentStep = isAuthorized ? (data?.progressBar as number) || 1 : 0;

  // methods
  const handleModalClick = (stepKey: string) => {
    if (!isAuthorized && stepKey !== "create-account") {
      setModalConfig(null);
      setState({ open: true });
      
      return;
    }

    setModalConfig(null);

    switch (stepKey) {
      case "create-account":
        router.push("/registration");
        break;
      case "search":
        router.push("/go-search");
        break;
      case "create-campaign":
        router.push("/campaigns?createNewCampaign=true");
        break;
      case "add-space":
        router.push("/go-search");
        break;
      case "option-book":
        setIsSaveToShortListModalOpen(true);
        break;
    }
  }

  // effects
  useEffect(() => {
    if ((!data || (data && data.progressBar < 3)) && isNotMobile) {
      setIsOpen(true);
    } else if (data && data.progressBar >= 3) {
      setIsOpen(false);
    }
  }, [data?.progressBar]);

  // renders
  return (
    <Collapse
      className={styles["locl-getting-started"]}
      activeKey={isOpen ? ["getting-started"] : []}
      onChange={(e) => setIsOpen(!!e.length)}
      data-testid="locl-getting-started"
    >
      <Collapse.Panel
        showArrow
        key="getting-started"
        header={<GettingStartedPreview percentage={MAP_PERCENTAGE[currentStep]} progress={currentStep + 1}/>}
      >
        <div className={styles["locl-getting-started__list"]} data-testid="locl-getting-started-inner">
          {
            GETTING_STARTED_STEPS.map((step, index) => (
              <GettingStartedStep
                key={index}
                pass={!!(index < currentStep)}
                active={index === currentStep}
                title={step.title}
                label={step.label}
                stepKey={step.stepKey}
                onLearnMore={() => setModalConfig({
                  title: step.modalTitle,
                  src: step.video,
                  open: true,
                  buttonText: step.buttonText,
                  modalHint: step.modalHint as string,
                  stepKey: step.stepKey
                })}
              />
            ))
          }
        </div>
        <GettingStartedModal
          open={modalConfig !== null}
          onCancel={() => setModalConfig(null)}
          src={modalConfig?.src as string}
          title={modalConfig?.title}
          buttonText={modalConfig?.buttonText as string}
          modalHint={modalConfig?.modalHint as string}
          onClick={() => handleModalClick(modalConfig?.stepKey as string)}
        />
        <SelectCampaignModal
          open={isSaveToShortListModalOpen}
          onCancel={() => setIsSaveToShortListModalOpen(false)}
        />
      </Collapse.Panel>
    </Collapse>
  );
}

export default GettingStarted;

